import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  Dimensions,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  Image,
  ActivityIndicator,
  RefreshControl,
  FlatList,
  Linking,
  Alert,
  Platform,
  Pressable,
  Modal,
  TextInput
} from 'react-native';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { getTukarPoinRiwayat, getPaymentInfo, cancelRedemption } from '../services/rewardsApi';
import { Ionicons } from '@expo/vector-icons';
import {
  TUKAR_POIN_COLORS,
  formatPoinNumber,
  statusMeta,
  paymentUrl,
  linkajaQrUrl,
  hasUnpaidOngkir,
  historyOrderPath,
  isPhysicalHistoryItem,
  isVoucherReward,
  paymentExpireHint,
  processingSlaCopy,
  paymentExpiredAtTs,
} from '../utils/tukarPoin';
import { openZoolyfeShopUrl } from '../services/zoolyfeSsoService';
import AppHeader from '../components/AppHeader';
import PaymentCountdownItem from '../components/PaymentCountdownItem';
import { useCustomAlertModal } from '../components/CustomAlertModal';

const { width, height } = Dimensions.get('window');

const formatHistoryDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.replace(/(\d{2}):(\d{2})(?::\d{2})?$/, '$1:$2');
};

export default function TukarPoinHistoryScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const caller = route.params?.caller || '';
  const { 
    customAlertModal, 
    showCustomAlert, 
    hideCustomAlert, 
    handleCustomAlertConfirm
  } = useCustomAlertModal();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [currentTs, setCurrentTs] = useState(Date.now());

  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationReasonNote, setCancellationReasonNote] = useState('');
  const [cancellationModal, setCancellationModal] = useState({
    redemption: null,
    visible: false,
  });

  const showCancellationModal = (redemption = null) => {
    setCancellationReason('');
    setCancellationModal({
      redemption,
      visible: true,
    })
  }

  const hideCancellationModal = () => {
    setCancellationModal({
      redemption: null,
      visible: false,
    })
  }

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await getTukarPoinRiwayat(1, 100);
      const data = response?.data && !Array.isArray(response.data) ? response.data : {};
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      Alert.alert('Gagal memuat riwayat', e.message || 'Coba lagi.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      load(itemsRef.current.length > 0);
    }, [load]),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTs( Date.now() );
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const payOngkir = async (item) => {
    try {
      const paymentInfo = await getPaymentInfo(item.id);
      if( paymentInfo.status === `success` ) {

        const qr = linkajaQrUrl(item);
        if (qr) {
          setQrUrl(qr);
          return;
        }

        const url = paymentUrl(item);
        if (!!url) {
          try {
            await Linking.openURL(url);
          } catch (e) {
            showCustomAlert(
              `Pemberitahuan`,
              `Terjadi kesalahan saat membuka tautan pembayaran: ${(e.message || ``)}`,
              `error`
            );
          }
          return;
        }
        
        showCustomAlert(
          `Pemberitahuan`,
          `Info pembayaran tidak ditemukan`,
          `error`
        );

      } else {
        showCustomAlert(
          `Pemberitahuan`,
          paymentInfo.message || `Gagal mendapatkan info pembayaran`,
          `error`
        );
      }
    } catch(e) {
      showCustomAlert(
        `Pemberitahuan`,
        e.message || `Terjadi kesalahan saat mendapatkan info pembayaran`,
        `error`
      );
    }
  };

  const openShopOrder = async (item) => {
    try {
      await openZoolyfeShopUrl(historyOrderPath(item));
    } catch (e) {
      Alert.alert('Tidak bisa membuka pesanan', e.message || 'Coba lagi.');
    }
  };

  const cancel = async (redemption) => {
    try {
      const cancelResponse = await cancelRedemption(redemption.id, cancellationReason);
      if( cancelResponse.status === `success` ) {
        showCustomAlert(
          `Pemberitahuan`,
          `Penukaran poin berhasil dibatalkan`,
          `success`
        );
        load(false);
      } else {
        showCustomAlert(
          `Pemberitahuan`,
          cancelResponse.message || `Gagal membatalkan penukaran poin`,
          `error`
        );
      }
    } catch(e) {
      console.log(e);
      showCustomAlert(
        `Pemberitahuan`,
        e.message || `Terjadi kesalahan saat membatalkan penukaran poin`,
        `error`
      );
    }
  }

  const handleBackPress = () => {
    if( caller && caller === 'tukar poin' ) {
      navigation.navigate('TukarPoin');
    } else {
      navigation.goBack();
    }
  }

  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      if( caller && caller === 'tukar poin' ) {
        e.preventDefault();
        navigation.navigate('TukarPoin');
      }
    });

    return unsubscribe;
  }, [navigation]);

  const renderItem = ({ item }) => {
    const status = statusMeta(item.status_redeem, item);
    const cancelled = item.status_redeem === 'dibatalkan';
    const unpaid = hasUnpaidOngkir(item);
    const expiry = unpaid ? paymentExpireHint(item) : '';
    const dueTs = unpaid ? paymentExpiredAtTs(item) : null;
    const pointSign = cancelled ? '' : '-';
    const hasImage = !!item.gambar_url;
    const physical = isPhysicalHistoryItem(item);
    const voucher = isVoucherReward(item);
    const canOpenShop = physical && !cancelled && !unpaid;
    const slaCopy = processingSlaCopy(item);
    const cancelable = item.is_user_cancelable || false;

    const cardInner = (
      <>
        {hasImage ? (
          <View style={styles.imageWrap} pointerEvents="none">
            <Image source={{ uri: item.gambar_url }} style={styles.image} resizeMode="contain" />
          </View>
        ) : (
          <View style={styles.imagePlaceholder} pointerEvents="none" />
        )}
        <View style={styles.body} pointerEvents="box-none">
          <Text style={styles.title} numberOfLines={2} pointerEvents="none">{item.nama_reward}</Text>
          {!!(item.product_varian || item.product_ukuran || item.product_sku) ? (
            <Text style={styles.skuLine} numberOfLines={1} pointerEvents="none">
              {[item.product_varian, item.product_ukuran, item.product_sku].filter(Boolean).join(' · ')}
            </Text>
          ) : null}
          {unpaid && currentTs > dueTs ? (
              <Text style={[styles.status, { color: "#F43F5E" }]} pointerEvents="none">Dibatalkan</Text>
            ) : (
              <Text style={[styles.status, { color: status.color }]} pointerEvents="none">{status.label}</Text>
          )}
          {!!item.catatan_customer && (
            <Text style={styles.reason} pointerEvents="none">Catatan: {item.catatan_customer}</Text>
          )}
          {cancelled && !!item.catatan && (
            <Text style={styles.reason} pointerEvents="none">Alasan: {item.catatan}</Text>
          )}
          {slaCopy ? (
            <Text style={styles.slaNote} pointerEvents="none">{slaCopy}</Text>
          ) : null}
          <Text style={styles.date} pointerEvents="none">{formatHistoryDate(item.tanggal_redeem)}</Text>
          {expiry ? <PaymentCountdownItem due={dueTs} now={currentTs} /> : null}

          <View style={styles.actionBtnWrapper}>
            {cancelable && (voucher || currentTs < dueTs) && (
              <TouchableOpacity
                style={[styles.actionBtn, {backgroundColor: '#EF4444'}]}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  showCancellationModal(item);
                }}
              >
                <Text style={styles.actionBtnText}>Batalkan</Text>
              </TouchableOpacity>
            )}
            {unpaid && currentTs < dueTs && (
              <TouchableOpacity
                style={styles.actionBtn}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  payOngkir(item);
                }}
              >
                <Text style={styles.actionBtnText}>Bayar ongkir</Text>
              </TouchableOpacity>
            )}
          </View>

          {item.id_kupon_detail ? (
            <TouchableOpacity
              style={[styles.actionBtn, styles.actionBtnSecondary]}
              onPress={(e) => {
                e?.stopPropagation?.();
                navigation.navigate('ShowCoupon', { coupon_id: item.id_kupon_detail });
              }}
            >
              <Text style={styles.actionBtnText}>Lihat voucher</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        <Text style={[styles.points, cancelled && styles.pointsCancelled]} pointerEvents="none">
          {pointSign}{formatPoinNumber(item.harga_poin)}
        </Text>
      </>
    );

    return (
      <View style={styles.rowWrap}>
        {canOpenShop ? (
          <Pressable
            style={styles.row}
            onPress={() => openShopOrder(item)}
          >
            {cardInner}
          </Pressable>
        ) : (
          <View style={styles.row}>
            {cardInner}
          </View>
        )}
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.safe}>
      <AppHeader 
        title="Riwayat Tukar Poin" 
        onBack={() => handleBackPress()}
        right={(
          <TouchableOpacity onPress={load} hitSlop={12}>
            <Ionicons name="reload" size={24} color="#1E293B" />
          </TouchableOpacity>
        )}
      />
      {loading && items.length === 0 ? (
        <View style={styles.center}>
          <ActivityIndicator color={TUKAR_POIN_COLORS.gold} />
        </View>
      ) : (
        <FlatList
          data={items}
          keyExtractor={(item) => String(item.id)}
          renderItem={renderItem}
          ItemSeparatorComponent={() => <View style={styles.divider} />}
          contentContainerStyle={styles.list}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                setRefreshing(true);
                load(true);
              }}
              tintColor={TUKAR_POIN_COLORS.gold}
            />
          }
          ListEmptyComponent={
            <View style={styles.center}>
              <Text style={styles.emptyTitle}>Belum ada penukaran</Text>
              <Text style={styles.emptySub}>Klaim reward dari katalog Tukar Poin.</Text>
            </View>
          }
        />
      )}

      <Modal visible={!!qrUrl} transparent animationType="fade" onRequestClose={() => setQrUrl('')}>
        <View style={styles.qrBackdrop}>
          <View style={styles.qrCard}>
            <Text style={styles.qrTitle}>Bayar ongkir via QRIS</Text>
            <Text style={styles.qrSub}>Scan QR LinkAja untuk menyelesaikan pembayaran.</Text>
            {qrUrl ? (
              <Image source={{ uri: qrUrl }} style={styles.qrImage} resizeMode="contain" />
            ) : null}
            <TouchableOpacity style={styles.qrBtn} onPress={() => setQrUrl('')}>
              <Text style={styles.qrBtnText}>Tutup</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Cancellation Reason Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={cancellationModal.visible}
        onRequestClose={()=>{}}
      >
        <View style={styles.cancellationModalOverlay}>
          <View style={styles.cancellationModalContainer}>

            <View style={styles.cancellationModalRow}>
              <Text style={styles.cancellationModalHeader}> Batalkan Penukaran Poin </Text>
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={() => {
                  hideCancellationModal();
                }}
              >
                <Ionicons name="close" size={20} color="#000000" />
              </TouchableOpacity>
            </View>

            <View style={{width: '100%'}}>
              <TextInput
                style={styles.cancellationModalInput}
                value={cancellationReason}
                onChangeText={setCancellationReason}
                multiline={true}
                numberOfLines={3}
                placeholder="Ketikkan alasan pembatalanmu disini"
                placeholderTextColor="#94A3B8"
              />
              {cancellationReasonNote && (
                <Text style={styles.cancellationModalNote}> {cancellationReasonNote} </Text>
              )}
            </View>

            <View style={styles.cancellationModalRow}>
              <TouchableOpacity
                style={styles.cancellationModalButton}
                activeOpacity={0.7}
                onPress={() => {
                  hideCancellationModal();
                }}
              >
                <Text style={styles.cancellationModalButtonText}> Kembali </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.cancellationModalButton, {backgroundColor: '#EF4444', borderRadius: 6}]}
                activeOpacity={0.7}
                onPress={()=> {
                  let msg = ``;

                  if( !cancellationReason ) {
                    msg = `Alasan pembatalan belum diisi`;
                  }

                  if( !cancellationModal.redemption ) {
                    msg = `Penukaran poin yang akan dibatalkan belum dipilih`;
                  }

                  if( !!msg ) {
                    setCancellationReasonNote(msg);
                    setTimeout( () => {
                      setCancellationReasonNote(``);
                    }, 2000 );
                    return;
                  }

                  hideCancellationModal();
                  showCustomAlert(
                    `Konfirmasi`,
                    `Apakah Kamu yakin akan membatalkan penukaran poin ini? Pembatalan bersifat permanen dan tidak dapat diaktifkan kembali`,
                    `success`,
                    () => {
                      cancel( cancellationModal.redemption )
                    }
                  );
                }}
              >
                <Text style={[styles.cancellationModalButtonText, {color: '#FFFFFF'}]}> Batalkan </Text>
              </TouchableOpacity>
            </View>

          </View>
        </View>
      </Modal>
      {/* End of Reason Modal */}

      {customAlertModal}

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#FFFFFF' },
  list: { paddingHorizontal: 20, paddingTop: 8, paddingBottom: Platform.OS === 'ios' ? 40 : 24 },
  rowWrap: { paddingVertical: 16 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  imageWrap: {
    width: 90,
    height: 90,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
  },
  image: {
    width: 90,
    height: 90,
  },
  imagePlaceholder: {
    width: 87,
    height: 54,
    borderRadius: 8,
    backgroundColor: '#E5E7EB',
  },
  body: { flex: 1, minWidth: 0, gap: 6 },
  title: {
    fontFamily: 'Poppins-Medium',
    fontSize: 12,
    fontWeight: '500',
    color: '#000',
    lineHeight: 16,
  },
  skuLine: {
    fontFamily: 'Poppins',
    fontSize: 11,
    color: '#64748B',
    lineHeight: 16,
  },
  points: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 16,
    fontWeight: '600',
    color: '#F59E0B',
    lineHeight: 22,
    flexShrink: 0,
  },
  pointsCancelled: {
    color: '#D1D5DB',
  },
  status: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 20,
  },
  reason: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#1E293B',
    lineHeight: 16,
  },
  slaNote: {
    fontFamily: 'Poppins',
    fontSize: 11,
    color: '#64748B',
    lineHeight: 16,
  },
  date: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#94A3B8',
    lineHeight: 16,
  },
  expiry: {
    fontFamily: 'Poppins',
    fontSize: 11,
    color: '#D97706',
    lineHeight: 16,
  },
  actionBtnWrapper: {
    marginTop: 2, 
    flexDirection: 'row', 
    flexWrap: 'wrap', 
    gap: 4
  },
  actionBtn: {
    alignSelf: 'flex-start',
    backgroundColor: '#1E293B',
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  actionBtnSecondary: {
    backgroundColor: '#2563EB',
  },
  actionBtnText: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 11,
    color: '#FFFFFF',
    lineHeight: 14,
  },
  divider: {
    height: 1,
    backgroundColor: '#F1F5F9',
  },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  emptyTitle: { fontFamily: 'Poppins-SemiBold', fontSize: 16, color: TUKAR_POIN_COLORS.navy },
  emptySub: { fontFamily: 'Poppins', fontSize: 13, color: TUKAR_POIN_COLORS.slate, marginTop: 4, textAlign: 'center' },
  qrBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  qrCard: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
  },
  qrTitle: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 16,
    color: '#1E293B',
    textAlign: 'center',
  },
  qrSub: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 6,
    marginBottom: 16,
  },
  qrImage: {
    width: 220,
    height: 220,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
  },
  qrBtn: {
    marginTop: 16,
    backgroundColor: '#1E293B',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 24,
    alignSelf: 'stretch',
    alignItems: 'center',
  },
  qrBtnText: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 14,
    color: '#FFFFFF',
  },

  cancellationModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: 0,
    zIndex: 10000,
  },
  cancellationModalContainer: {
    flexDirection: 'column',
    gap: 16,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    width: '100%',
    maxWidth: Math.min(430, width),
    padding: 16,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  cancellationModalRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancellationModalHeader: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 20,
    color:'#000000',
  },
  cancellationModalInput: {
    flex: 1,
    width: '100%',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'ios' ? 10 : 8,
    fontFamily: 'Poppins-Medium',
    fontSize: 12,
    color: '#0F172A',
  },
  cancellationModalNote: {
    width: '100%',
    fontFamily: 'Poppins-Medium',
    fontSize: 12,
    color: '#EF4444',
  },
  cancellationModalButton: {
    width: 'fit-content',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  cancellationModalButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 12,
    lineHeight: 16,
    textAlign: 'center',
  },
});
